import type {
  BlogPostListItemType,
  BlogPostViewInterface,
} from './normalizeBlog';

export interface BlogCategoryMetaInterface {
  name: string;
  slug: string;
  explainer?: string | undefined;
}

export interface BlogCategoryInterface extends BlogCategoryMetaInterface {
  posts: BlogPostListItemType[];
}

interface BlogsIndexInterface {
  categories: BlogCategoryMetaInterface[];
  posts: BlogPostListItemType[];
}

// The index and per-post files are written by `nx run @sc/marketing:blogs`
// before the Vite builds. Glob imports so a checkout that hasn't pulled yet
// degrades to an empty blog instead of a build failure.
const indexModules = import.meta.glob<{ default: BlogsIndexInterface }>(
  './blogs.cache.json',
  { eager: true }
);

// Newest first — the pull script writes posts ordered by -sys.createdAt.
const { categories, posts } = Object.values(indexModules)[0]?.default ?? {
  categories: [],
  posts: [],
};

// Every category with all of its posts; empty categories are dropped.
const categoryList: BlogCategoryInterface[] = categories
  .map((category) => ({
    ...category,
    posts: posts.filter((post) => post.categorySlug === category.slug),
  }))
  .filter((category) => category.posts.length > 0);

const POSTS_PER_CATEGORY = 6;

// The blog index shows each category's most recent posts only.
export const blogCategories: BlogCategoryInterface[] = categoryList.map(
  (category) => ({
    ...category,
    posts: category.posts.slice(0, POSTS_PER_CATEGORY),
  })
);

export const findBlogCategoryBySlug = (
  slug: string
): BlogCategoryInterface | undefined =>
  categoryList.find((category) => category.slug === slug);

// Lazy glob: each post's full payload (body + its own entries/assets) is its
// own content-hashed chunk, loaded only when that post is visited.
const postModules = import.meta.glob<{ default: BlogPostViewInterface }>(
  './blogs/*.json'
);

export const findBlogPostBySlug = async (
  slug: string
): Promise<BlogPostViewInterface | undefined> => {
  const load = postModules[`./blogs/${slug}.json`];

  if (!load) return undefined;

  return (await load()).default;
};
